import api from "../api/axiosInstance";
import { store } from "../redux/store";

const API_BASE = process.env.REACT_APP_API_URL;

// Streams a newline-delimited JSON POST response (not a plain axios call,
// since axios doesn't expose a readable stream in the browser) — calls
// onProgress for every non-terminal event and resolves with the {type:"done"}
// payload. Used for any long-running admin/power-user action that reports
// live progress (scan-for-matches, bulk CSV import, …).
const streamNdjsonPost = async (path, body, onProgress) => {
  const token = store.getState().auth.accessToken;
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok || !response.body) {
    let message = "Request failed";
    try {
      const errJson = await response.json();
      message = errJson.message || message;
    } catch {
      // response wasn't JSON — fall back to the generic message
    }
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let final = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop(); // last entry may be a half-received line — keep it for next chunk

    for (const line of lines) {
      if (!line.trim()) continue;
      const evt = JSON.parse(line);
      if (evt.type === "error") {
        throw new Error(evt.message || "Request failed");
      }
      if (evt.type === "done") {
        final = evt;
      } else {
        onProgress?.(evt);
      }
    }
  }

  if (!final) throw new Error("Stream ended unexpectedly");
  return final;
};

const shipmentService = {
  getAll: (params) => api.get("/shipments", { params }),
  getOne: (id) => api.get(`/shipments/${id}`),
  create: (data) => api.post("/shipments", data),
  update: (id, data) => api.patch(`/shipments/${id}`, data),
  // Admin-only edit history — never called from any customer-facing page.
  getActivityLog: (id) => api.get(`/shipments/${id}/activity-log`),
  updateStatus: (id, status, note) =>
    api.patch(`/shipments/${id}/status`, { status, note }),
  updateItemStatus: (id, itemId, status, note, notifyCustomer = true) =>
    api.patch(`/shipments/${id}/items/${itemId}/status`, {
      status,
      note,
      notifyCustomer,
    }),
  delete: (id) => api.delete(`/shipments/${id}`),
  getStats: () => api.get("/shipments/stats"),

  // Items
  addItems: (id, items) => api.post(`/shipments/${id}/items`, { items }),
  updateItem: (id, itemId, data) =>
    api.patch(`/shipments/${id}/items/${itemId}`, data),
  removeItem: (id, itemId) => api.delete(`/shipments/${id}/items/${itemId}`),
  // Admin manual weight/CBF override for one item — pass null to clear an
  // override back to the auto-computed even split across its D/R group.
  setItemMeasurements: (id, itemId, { weight, cbf }) =>
    api.patch(`/shipments/${id}/items/${itemId}/measurements`, { weight, cbf }),
  // Either trackingNumbers or itemIds (or both) may be provided — itemIds
  // catches items with no tracking number yet, which trackingNumbers alone
  // can't select.
  // scanMode ("in" | "out") is only passed by the Scan In / Scan Out page —
  // it makes the server write a ScanLog entry per item (who scanned what,
  // in which direction). Omit it for any other bulk status change.
  bulkUpdateTrackingStatus: (trackingNumbers, itemStatus, itemIds, scanMode) =>
    api.post("/shipments/bulk-update-tracking-status", { trackingNumbers, itemStatus, itemIds, scanMode }),
  // Read-only, single item — used by the Cayman warehouse scan-in page for
  // instant per-scan feedback before the batch is committed. targetStatus is
  // whichever item status the admin has selected to bulk-assign.
  lookupItem: (trackingNumber, targetStatus) =>
    api.get("/shipments/items/lookup", { params: { trackingNumber, targetStatus } }),

  // Partial/prefix tracking-number match, scoped server-side to items
  // between "Package Received" and "Processing in GCM" — powers the Scan
  // In/Out manual-entry autocomplete (camera/scanner sometimes can't read a
  // damaged barcode at all). `mode` ("in"/"out") scopes the eligible status
  // range to that scan direction — see shipmentItemController.searchItemsByTrackingNumber.
  searchItems: (q, mode) => api.get("/shipments/items/search", { params: { q, mode } }),

  // Every item assigned to a sailing, grouped Sailing → Shipment → Item —
  // the admin "All Items" bulk-status page.
  listItemsBySailing: (params) => api.get("/shipments/items/by-sailing", { params }),

  // Dock receipts — trackingNumbers may be a single string or an array, to
  // consolidate several tracking numbers onto one D/R in a single call.
  listDockReceiptItems: (params) => api.get("/shipments/items/dock-receipts", { params }),
  assignDockReceipt: (trackingNumbers, dockReceiptNumber, extra) =>
    api.patch("/shipments/items/dock-receipt", {
      trackingNumbers: Array.isArray(trackingNumbers) ? trackingNumbers : [trackingNumbers],
      dockReceiptNumber,
      ...extra,
    }),
  // D/R-only correction pass — no tracking number needed, just
  // {dockReceiptNumber, weight, cbf} per row, for D/Rs already linked to items.
  bulkUpdateDockReceiptMeasurements: (rows) =>
    api.patch("/shipments/items/dock-receipt-measurements", { rows }),
  // Every customer-submitted purchase invoice for shipments that have an
  // item under this D/R, zipped together — same idea as the sailing
  // manifest's "download purchase invoices."
  getDockReceiptInvoicesZip: (drNumber) =>
    api.get(`/shipments/items/dock-receipts/${encodeURIComponent(drNumber)}/invoices.zip`, {
      responseType: "blob",
    }),

  // Ready to sail — all items on the shipment have a D/R assigned
  listReadyToSail: (params) => api.get("/shipments/ready-to-sail", { params }),
  sendDockReceiptEmail: (id, to) => api.post(`/shipments/${id}/send-dock-receipt-email`, { to }),
  // One email covering several ready-to-sail shipments — pass shipmentIds
  // to target exactly those (regardless of sent/pending status), or omit
  // it to fall back to every shipment matching filter/search (same params
  // as listReadyToSail), not just the current page.
  sendBulkDockReceiptEmail: (to, { filter, search, shipmentIds } = {}) =>
    api.post("/shipments/ready-to-sail/send-dock-receipt-email", { to, filter, search, shipmentIds }),

  // "Consolidate Items" / "Assign Dock Receipts to Sailing" — pure email
  // actions over an admin-picked set of items ({ shipmentId, itemId }[]).
  // Preview renders the exact email without sending it.
  previewConsolidateEmail: (items) =>
    api.post("/shipments/ready-to-sail/consolidate-email/preview", { items }),
  sendConsolidateEmail: (items, to) =>
    api.post("/shipments/ready-to-sail/consolidate-email/send", { items, to }),
  previewAssignDrEmail: (items) =>
    api.post("/shipments/ready-to-sail/assign-dr-email/preview", { items }),
  sendAssignDrEmail: (items, to) =>
    api.post("/shipments/ready-to-sail/assign-dr-email/send", { items, to }),

  // Reminds a shipment's customer to upload their purchase invoice.
  sendInvoiceReminder: (id) => api.post(`/shipments/${id}/send-invoice-reminder`),

  scanForUnclaimedMatches: (onProgress) =>
    streamNdjsonPost("/shipments/scan-unclaimed-matches", undefined, onProgress),

  // Bulk import (CSV) — "power user" feature, gated server-side by
  // canBulkImportShipments. Sample download uses fetch + Blob (not a plain
  // <a href>) because auth here is Bearer-token only, no auth cookie a
  // direct browser navigation could ride along on.
  downloadBulkImportSample: async () => {
    const token = store.getState().auth.accessToken;
    const response = await fetch(`${API_BASE}/shipments/bulk-import/sample-csv`, {
      method: "GET",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error("Failed to download sample CSV");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "shipment-bulk-import-sample.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // `allowSharedTracking` — the manual Bulk Add form's "Single tracking for
  // all orders" mode deliberately reuses one tracking number across every
  // item in the batch; the CSV upload never sets this (same strict
  // one-tracking-number-per-item validation as always).
  // `customer` — admin-only: which customer these shipments are being
  // imported for (the server ignores this for a non-admin caller, who
  // always imports as themselves).
  bulkImportPreview: (rows, allowSharedTracking = false, customer = undefined) =>
    api.post("/shipments/bulk-import/preview", { rows, allowSharedTracking, customer }),

  // Streams live progress — {type:"progress", shipmentsDone, totalShipments,
  // itemsDone, totalItems, createdCount, failedCount} — while shipments are
  // being created, so the UI can show "10 (15 items) of 20 (30 items)
  // shipments uploaded" instead of a plain spinner.
  bulkImportConfirm: (groups, onProgress, tin, allowSharedTracking = false, customer = undefined) =>
    streamNdjsonPost("/shipments/bulk-import", { groups, tin, allowSharedTracking, customer }, onProgress),
};

export default shipmentService;
